import {
  Box,
  Button,
  Modal
} from '@platform/ui-library';
import {
  FC,
  useEffect,
  useState
} from 'react';
import { useServices } from '@app/hooks';
import { Input } from '../Input';
import { FileUploader, FileUploaderProps } from './file-uploader';

type UploadedFile = {
  file: File;
  fileName: string;
  text: string;
};

export type FileUploaderModalProps = (
  FileUploaderProps
  & {
    className?: string;
    isOpen: boolean;
    labels?: {
      /** Label to use for the header of the modal. */
      header?: string;
    };
    /** Allows user to set a fileName on the @see template @default true. */
    isFileNameEditable?: boolean;
    /** Called when the user uploads or cancels the process. */
    onComplete: (item: UploadedFile | null) => void;
  }
);

export const FileUploaderModal: FC<FileUploaderModalProps> = ({
  className,
  isFileNameEditable = true,
  isOpen,
  labels,
  onComplete,
  onParse,
  onUpload,
  template,
  ...fileUploaderProps
}) => {
  const { errorMessageService } = useServices();
  const [fileName, setFileName] = useState(template?.fileName ?? '');
  const [file, setFile] = useState<File | null>();
  const [text, setText] = useState<string | null>();

  const showError = (message: string) => {
    errorMessageService.addError({
      message,
      title: 'Import Error'
    });
  };

  useEffect(() => {
    if (!isOpen) {
      setFileName(template?.fileName ?? '');
      setFile(null);
      setText(null);
    }
  }, [isOpen]);

  return (
    <Modal className={className ?? 'ui-box-small'} isOpen={isOpen}>
      <Box theme="noPadding">
        <div className="box header">
          <h2>{labels?.header ?? 'Upload File'}</h2>
        </div>
        <div className="box content">
          {(!isFileNameEditable) ? null : (
            <Input
              label="File Name"
              name="fileName"
              onChange={setFileName}
              value={fileName}
            />
          )}
          <FileUploader
            {...fileUploaderProps}
            onParse={(newText) => {
              setText(newText);
              onParse?.(newText);
            }}
            onUpload={(file) => {
              if (
                (!template || fileName === template.fileName)
                && isFileNameEditable
              ) {
                setFileName(file.name);
              }
              setFile(file);
              onUpload?.(file);
            }}
            template={(!template) ? null : {
              ...template,
              fileName
            }}
          />
        </div>
        <div className="box footer no-border">
          <Button
            onClick={() => {
              onComplete(null);
            }}
            theme="outline"
            type="button"
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!fileName && isFileNameEditable) {
                return showError('File name is unset!');
              } else if (!file || !text) {
                return showError('No file selected!');
              }
              onComplete({ file, fileName, text });
            }}
            title={(file) ? undefined : 'Please upload a file first'}
            type="button"
          >
            Import
          </Button>
        </div>
      </Box>
    </Modal>
  );
};
